import { useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { REPORT_REASONS, reportPricingComplaint } from '../utils/pricingReports';
import haptics from '../utils/haptics';
import BottomSheet from './BottomSheet';

/**
 * Trigger B of the pricing repair loop — the user says the price is wrong.
 *
 * The reason chips carry the weight here. Free text does not cluster, so a
 * hundred notes saying "this looks off" stay a hundred separate problems. A
 * reason code plus the card's set and finish is what lets one fix close out
 * every report that shares the same cause.
 *
 * The note is still offered, because the chips will not cover everything and
 * the diagnosis reads it.
 */
export default function ReportPricingSheet({ isOpen, onClose, card, context, theme }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);   // 'saved' | 'duplicate'
  const [error, setError] = useState(null);

  const s = styles(theme);

  const close = () => {
    setReason(null);
    setNote('');
    setResult(null);
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!reason || sending) return;
    setSending(true);
    setError(null);
    haptics.tap();
    // Built here rather than on every render of the page behind this sheet:
    // the snapshot walks the evidence array, and the card detail screen
    // re-renders far more often than anyone reports a price.
    const snapshot = typeof context === 'function' ? context() : (context || {});
    const status = await reportPricingComplaint(card, reason, note, snapshot);
    setSending(false);

    if (status === 'saved' || status === 'duplicate') {
      haptics.success();
      setResult(status);
      setTimeout(close, 1800);
      return;
    }

    haptics.warn();
    setError(
      status === 'signed_out'
        ? 'You are signed out, so this did not send. Sign in and report it again.'
        : 'That did not send. Check your connection and try again.'
    );
  };

  return (
    <BottomSheet open={isOpen} onClose={close} overlayStyle={s.overlay} sheetStyle={s.sheet} label="Report this pricing">
        {result ? (
          <div style={s.done}>
            <div style={s.doneIcon}><Check size={22} /></div>
            <div style={s.title}>
              {result === 'duplicate' ? 'Already reported today' : 'Thanks, that helps'}
            </div>
            <div style={s.sub}>
              {result === 'duplicate'
                ? 'This card is already on the list for today, so nothing new was filed.'
                : 'We look at these in batches and fix the cause, not just this card.'}
            </div>
          </div>
        ) : (
          <>
            <div style={s.head}>
              <div style={s.headIcon}><AlertTriangle size={16} /></div>
              <div>
                <div style={s.title}>Report this pricing</div>
                <div style={s.sub}>{card?.name}{card?.set_number ? ` · ${card.set_number}` : ''}</div>
              </div>
            </div>

            <div style={s.label}>What looks wrong?</div>
            <div style={s.chips}>
              {REPORT_REASONS.map((r) => (
                <button
                  key={r.code}
                  style={{ ...s.chip, ...(reason === r.code ? s.chipOn : {}) }}
                  onClick={() => { haptics.select(); setReason(r.code); }}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <div style={s.label}>Anything else? (optional)</div>
            <textarea
              style={s.note}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="It should be the holo version, not normal"
              maxLength={1000}
              rows={3}
            />

            {error && (
              <div style={s.error}>{error}</div>
            )}

            <div style={s.actions}>
              <button style={s.cancel} onClick={close}>Cancel</button>
              <button
                style={{ ...s.submit, ...(reason ? {} : s.submitOff) }}
                onClick={submit}
                disabled={!reason || sending}
              >
                {sending ? 'Sending...' : 'Send report'}
              </button>
            </div>
          </>
        )}
    </BottomSheet>
  );
}

const styles = (theme) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 11000,
    padding: '16px',
    paddingBottom: 'calc(24px + var(--safe-area-bottom, 0px))',
  },
  sheet: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '16px',
    padding: '16px',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  head: { display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '16px' },
  headIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '9px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: theme.warningBg,
    color: theme.warning,
  },
  title: { fontSize: '16px', fontWeight: '750', color: theme.text },
  sub: { fontSize: '12px', color: theme.textSecondary, marginTop: '2px' },
  label: {
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.textSecondary,
    margin: '4px 0 8px',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '16px' },
  chip: {
    border: `1px solid ${theme.border}`,
    background: 'transparent',
    color: theme.textSecondary,
    fontFamily: 'inherit', fontSize: '12.5px', fontWeight: '600', lineHeight: 1.2,
    padding: '8px 11px',
    borderRadius: '9px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  chipOn: {
    border: `1px solid ${theme.accent}`,
    background: theme.accentBg,
    color: theme.text,
  },
  note: {
    width: '100%',
    boxSizing: 'border-box',
    background: theme.bgSheet,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    color: theme.text,
    fontFamily: 'inherit', fontSize: '14px', fontWeight: '400', lineHeight: 1.45,
    padding: '10px',
    resize: 'vertical',
    marginBottom: '16px',
  },
  error: {
    fontSize: '13px',
    fontWeight: '600',
    lineHeight: 1.4,
    color: theme.warning,
    backgroundColor: theme.warningBg,
    border: `1px solid ${theme.warning}`,
    borderRadius: '10px',
    padding: '10px',
    marginBottom: '12px',
  },
  actions: { display: 'flex', gap: '9px' },
  cancel: {
    flex: 1,
    border: `1px solid ${theme.border}`,
    background: 'transparent',
    color: theme.textSecondary,
    fontFamily: 'inherit', fontSize: '14px', fontWeight: '650', lineHeight: 1,
    padding: '13px',
    borderRadius: '11px',
    cursor: 'pointer',
  },
  submit: {
    flex: 2,
    border: `1px solid ${theme.accent}`,
    background: theme.accent,
    color: '#fff',
    fontFamily: 'inherit', fontSize: '14px', fontWeight: '700', lineHeight: 1,
    padding: '13px',
    borderRadius: '11px',
    cursor: 'pointer',
  },
  submitOff: { opacity: 0.45, cursor: 'not-allowed' },
  done: { textAlign: 'center', padding: '18px 8px 12px' },
  doneIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
    backgroundColor: theme.successBg,
    color: theme.success,
  },
});
